import '../../components/warehouse-filter.css';
import { usePermissions } from '../../auth/permissions';
import React, { useEffect, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Box, Boxes, Check, ChevronDown, ClipboardList, House, Import, Package, Scale, Ship, UsersRound, Warehouse, Weight, PackageCheck } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { emptyDashboard, getDashboard } from './dashboardService';
import { getInventorySummary } from '../inventory/inventoryService';
import { listModuleWarehouses } from '../warehouses/warehouseService';
import usePersistentFilters, { validateFilters } from '../../components/usePersistentFilters';

const defaultFilters = { warehouse_id: '' };

const kpiMeta = [
  [UsersRound, 'Total Customer', 'customers'],
  [Box, 'In Planning', 'in_planning'],
  [ClipboardList, 'Planned', 'planned'],
  [House, 'Ready to Ship', 'ready_to_ship'],
  [Ship, 'Shipped', 'shipped'],
  [Import, 'Clearance', 'clearance'],
  [PackageCheck, 'Delivered', 'delivered'],
];

const tooltipStyle = { borderRadius: 8, border: '1px solid #e4edf5', fontSize: 11 };
const chartTick = { fill: '#7b8da3', fontSize: 10 };
const chartMargin = { top: 6, right: 8, left: -8, bottom: 0 };

function formatCount(value) {
  return (Number(value) || 0).toLocaleString('en-US');
}

const inventoryMeta = [
  [ClipboardList, 'blue', 'Inventory Records', 'inventory_records', ''],
  [Box, 'green', 'Total Products', 'unique_products', ' SKUs'],
  [Package, 'orange', 'Total Cartons', 'total_cartons', ''],
  [Weight, 'purple', 'Net Weight', 'net_weight', ' kg'],
  [Scale, 'slate', 'Gross Weight', 'gross_weight', ' kg'],
  [Box, 'cyan', 'Free Capacity', 'free_capacity', ' m\u00b3'],
];

export default function DashboardPage() {
  const { canRead } = usePermissions();
  const canOpenKpi = key => canRead(key === 'customers' ? 'Customers' : 'ContainerPlanning');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters, reconcileFilters, filtersReady] = usePersistentFilters('dashboardFilters', defaultFilters, {
    searchParams, setSearchParams: (params, options) => setSearchParams(params, { ...options, state: location.state }), urlKeys: { warehouse_id: 'warehouseId' },
  });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(emptyDashboard);
  const [inventory, setInventory] = useState(null);
  const [inventoryError, setInventoryError] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoaded, setWarehousesLoaded] = useState(false);
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  const [warehouseQuery, setWarehouseQuery] = useState('');
  const [showAllWarehouses, setShowAllWarehouses] = useState(false);
  useEffect(() => { if (!warehouseOpen) setShowAllWarehouses(false); }, [warehouseOpen]);
  const warehouseFilterRef = useRef(null);
  const warehouseTriggerRef = useRef(null);
  const warehouseId = filters.warehouse_id;
  const warehouseOptions = warehouses;
  const selectedWarehouse = warehouseOptions.find(option => String(option.id) === warehouseId);
  useEffect(() => {
    if (!warehouseOpen) return undefined;
    warehouseFilterRef.current?.querySelector('input[type="search"]')?.focus();
    function outside(event) {
      if (!warehouseFilterRef.current?.contains(event.target)) setWarehouseOpen(false);
    }
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [warehouseOpen]);
  function setWarehouseId(value) {
    setLoading(true);
    setFilters(current => ({ ...current, warehouse_id: value }));
  }
  function openKpi(key) {
    if (loading || !canOpenKpi(key)) return;
    if (key === 'customers') {
      navigate('/customers', { state: { fromDashboard: true, customerFilters: { warehouse_id: warehouseId } } });
      return;
    }
    const statuses = { in_planning: 'Planning', planned: 'Planned', ready_to_ship: 'ReadyToShip', shipped: 'Shipped', clearance: 'CustomsClearance', delivered: 'Delivered' };
    navigate('/container-planning', {
      state: { fromDashboard: true, containerPlanningFilters: { warehouse_id: warehouseId, status: statuses[key] } },
    });
  }

  useEffect(() => {
    let active = true;
    listModuleWarehouses('Dashboard').then(rows => { if (active) setWarehouses(rows); }).catch(() => {}).finally(() => { if (active) setWarehousesLoaded(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (warehousesLoaded) reconcileFilters(values => validateFilters(values, defaultFilters, { warehouse_id: warehouses.map(row => row.id) }, true, true));
  }, [warehouseId, warehouses, warehousesLoaded, reconcileFilters, filtersReady]);

  useEffect(() => {
    if (!warehousesLoaded || !filtersReady || (!warehouseId && warehouses.length)) return undefined;
    if (!warehouses.length) { setLoading(false); return undefined; }
    let active = true;
    const filters = warehouseId ? { warehouse_id: warehouseId } : {};
    setLoading(true);
    setInventory(null);
    setInventoryError(false);
    setData(emptyDashboard());
    getDashboard(filters)
      .then(async overview => {
        let summary = overview.inventory_summary;
        let summaryError = false;
        if (!summary) {
          try { summary = await getInventorySummary(filters, 'Dashboard'); }
          catch { summaryError = true; }
        }
        if (!active) return;
        setLoading(false);
        setData(overview);
        setInventory(summary || null);
        setInventoryError(summaryError);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
        setData(emptyDashboard());
        setInventory(null);
        setInventoryError(true);
      });
    return () => { active = false; };
  }, [warehouseId, warehouses, warehousesLoaded, filtersReady]);

  return <section className="dash">
    <div className="dash-title">
      <h1>Operations Overview</h1>
      <div className="dash-warehouse-filter" ref={warehouseFilterRef} onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setWarehouseOpen(false);
      }} onKeyDown={event => {
        if (event.target.matches('input[type="search"]') && ['Home', 'End'].includes(event.key)) return;
        if (event.key === 'Escape') {
          setWarehouseOpen(false); warehouseTriggerRef.current?.blur();
        }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (!warehouseOpen) { setWarehouseOpen(true); return; }
          const options = [...warehouseFilterRef.current.querySelectorAll('[role="option"]')];
          const index = options.indexOf(document.activeElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
            : index < 0 ? (event.key === 'ArrowUp' ? options.length - 1 : 0)
            : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options[next]?.focus();
        }
      }}>
        <span className="dash-warehouse-label" id="dashboard-warehouse-label"><Warehouse size={20} aria-hidden="true" />Warehouse</span>
        <div className="dash-warehouse-select">
          <div className={`dash-warehouse-trigger${warehouseOpen ? ' open' : ''}`} onClick={event => {
            if (!event.target.closest('input')) warehouseTriggerRef.current?.focus({ preventScroll: true });
          }}>
            <input className="dash-warehouse-search" type="search" role="combobox" ref={warehouseTriggerRef} aria-labelledby="dashboard-warehouse-label" aria-haspopup="listbox" aria-expanded={warehouseOpen} aria-controls={warehouseOpen ? 'dashboard-warehouse-options' : undefined} aria-autocomplete="list" autoComplete="off"
              value={warehouseOpen ? warehouseQuery : selectedWarehouse?.name || (warehouseId === 'all' ? 'All Warehouses' : 'Select Warehouse')} placeholder={warehouseOpen ? 'Search warehouses...' : undefined}
              onFocus={() => { if (!warehouseOpen) { setWarehouseQuery(''); setWarehouseOpen(true); } }}
              onClick={() => { if (!warehouseOpen) { setWarehouseQuery(''); setWarehouseOpen(true); } }}
              onChange={event => { setWarehouseQuery(event.target.value); if (!warehouseOpen) setWarehouseOpen(true); }} />
            <button type="button" tabIndex={-1} className="dropdown-list-arrow" aria-label="Show all warehouses" aria-haspopup="listbox" aria-expanded={warehouseOpen} aria-controls="dashboard-warehouse-options" onMouseDown={event => event.preventDefault()} onClick={() => { setWarehouseQuery(''); setShowAllWarehouses(true); setWarehouseOpen(true); warehouseTriggerRef.current?.focus({ preventScroll: true }); }}><ChevronDown size={14} aria-hidden="true" /></button>
          </div>
        </div>
        {warehouseOpen && (showAllWarehouses || warehouseQuery.trim()) && <div className="dash-warehouse-options" id="dashboard-warehouse-options" role="listbox" aria-labelledby="dashboard-warehouse-label">
          {(!warehouseQuery.trim() || 'all warehouses'.includes(warehouseQuery.trim().toLowerCase())) && <button type="button" role="option" aria-selected={!warehouseId} key="all" onClick={() => {
            setWarehouseId('all');
            setWarehouseQuery(''); setWarehouseOpen(false); warehouseTriggerRef.current?.focus();
          }}><Warehouse size={19} aria-hidden="true" /><span>All Warehouses</span>{!warehouseId && <Check size={16} aria-hidden="true" />}</button>}
          {warehouseOptions.filter(option => option.name.toLowerCase().includes(warehouseQuery.trim().toLowerCase())).map(option => <button type="button" role="option" aria-selected={String(option.id) === warehouseId} key={option.id} onClick={() => {
            if (String(option.id) !== warehouseId) setWarehouseId(String(option.id));
            setWarehouseQuery(''); setWarehouseOpen(false); warehouseTriggerRef.current?.focus();
          }}><Warehouse size={19} aria-hidden="true" /><span>{option.name}</span>{String(option.id) === warehouseId && <Check size={16} aria-hidden="true" />}</button>)}
          {!warehouseOptions.some(option => option.name.toLowerCase().includes(warehouseQuery.trim().toLowerCase())) && <p className="wh-menu-empty">No matching warehouses.</p>}
        </div>}
      </div>
    </div>
    <div className="kpi-row">
      {kpiMeta.map(([Icon, label, key]) => <article className="kpi-card" key={label} role="button" aria-label={label} aria-disabled={loading || !canOpenKpi(key)} aria-busy={loading} tabIndex={loading || !canOpenKpi(key) ? -1 : 0}
        onClick={() => openKpi(key)} onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openKpi(key); }
        }}>
        <header><span>{label}</span><span className="kpi-card-icon" aria-hidden="true"><Icon size={16} strokeWidth={2.5} /></span></header>
        <strong>{formatCount(data.kpis[key])}</strong>
      </article>)}
    </div>
    <div className="dash-grid">
      <article className="dash-card">
        <div className="dash-card-head">
          <div>
            <h2>Monthly Stock In</h2>
            <p>Inventory inflow (metric tons)</p>
          </div>
          <div className="dash-legend"><span><i style={{ background: '#17345f' }} />General Cargo</span><span><i style={{ background: '#7eb3ff' }} />Bulk &amp; Liquid</span></div>
        </div>
        <div className="chart-box">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.stock_in} barGap={3} barCategoryGap="32%" margin={chartMargin}>
              <CartesianGrid vertical={false} stroke="#eef3f8" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={chartTick} />
              <YAxis tickLine={false} axisLine={false} tick={chartTick} width={34} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="general" fill="#17345f" radius={[2, 2, 0, 0]} maxBarSize={12} />
              <Bar dataKey="bulk" fill="#7eb3ff" radius={[2, 2, 0, 0]} maxBarSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>
      <article className="dash-card dash-card-inventory" aria-labelledby="inventory-summary-title">
        <div className="dash-card-head inventory-summary-head">
          <Boxes size={22} aria-hidden="true" />
          <h2 id="inventory-summary-title">Inventory Summary</h2>
        </div>
        <div className="inventory-summary-grid" aria-busy={!inventory && !inventoryError}>
          {inventoryMeta.map(([Icon, tone, label, key, unit]) => <div className="inventory-summary-item" key={key}>
            <span className={`inventory-summary-icon inventory-summary-icon-${tone}`} aria-hidden="true"><Icon size={20} /></span>
            <div><span className="inventory-summary-label">{label}</span>
              <strong>{inventory ? `${formatCount(inventory[key])}${unit}` : '\u2014'}</strong>
            </div>
          </div>)}
        </div>
        {inventoryError && <p role="status">Unable to load inventory summary.</p>}
      </article>
    </div>
    <article className="dash-card dash-card-shipped">
      <div className="dash-card-head">
        <div>
          <h2>Containers Shipped</h2>
          <p>Monthly dispatch by container type</p>
        </div>
        <div className="dash-legend"><span><i style={{ background: '#17345f' }} />40ft HC</span><span><i style={{ background: '#7eb3ff' }} />20ft Dry Cargo</span></div>
      </div>
      <div className="chart-box-wide">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.shipped} barGap={3} barCategoryGap="36%" margin={chartMargin}>
            <CartesianGrid vertical={false} stroke="#eef3f8" />
            <XAxis dataKey="month" tickLine={false} axisLine={false} tick={chartTick} />
            <YAxis tickLine={false} axisLine={false} tick={chartTick} width={28} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="hc" fill="#17345f" radius={[2, 2, 0, 0]} maxBarSize={13} />
            <Bar dataKey="dry" fill="#7eb3ff" radius={[2, 2, 0, 0]} maxBarSize={13} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </article>
  </section>;
}
